import type {
  FileEntryCondition,
  FormSection,
  SelectOption,
  WorkflowField,
  WorkflowStep,
} from '@/api/workflows';

/**
 * Structural diff between two frozen workflow-step lists. Two versions of the
 * same workflow are compared by walking the trees keyed on the stable ids
 * emitted by the backend (`WorkflowStep.id`, `FormSection.id`, `FormRow.id`,
 * `FormColumn.id`, `WorkflowField.id`).
 *
 * A step / section / field that keeps its id across a version bump is treated
 * as the SAME entity (its content diff is a `modified` entry). Delete-and-add
 * on the same slot produces a paired `removed` + `added` — because the
 * originator effectively created a new entity, which is closer to how the
 * approver reads the change.
 *
 * The result is grouped BY STEP: each returned `StepChange` carries the
 * per-section + per-field changes below it. Unchanged branches are pruned so
 * the panel that renders this only shows what the approver actually needs to
 * look at.
 */

export type ChangeKind = 'added' | 'removed' | 'modified';

/**
 * One property that changed between two entities (step, section, or field).
 * `key` is machine-facing (drives an i18n string in the panel);
 * `before`/`after` are already-humanized strings the panel renders verbatim.
 */
export type PropertyChange = {
  key: string;
  before?: string;
  after?: string;
};

export type FieldChange = {
  fieldId: string;
  labelEn: string;
  labelFr: string;
  type: string;
  kind: ChangeKind;
  propertyChanges: PropertyChange[];
};

export type SectionChange = {
  sectionId: string;
  titleEn: string;
  titleFr: string;
  kind: ChangeKind;
  propertyChanges: PropertyChange[];
  fieldChanges: FieldChange[];
};

export type StepChange = {
  stepId: string;
  titleEn: string;
  titleFr: string;
  type: string;
  kind: ChangeKind;
  propertyChanges: PropertyChange[];
  sectionChanges: SectionChange[];
};

export type WorkflowDiff = {
  steps: StepChange[];
  hasChanges: boolean;
};

/** Top-level entry point. Diff two frozen step lists (previous vs current). */
export function diffWorkflowSteps(
  prev: readonly WorkflowStep[],
  next: readonly WorkflowStep[],
): WorkflowDiff {
  const prevById = new Map(prev.map((s) => [s.id, s]));
  const nextIds = new Set(next.map((s) => s.id));
  const steps: StepChange[] = [];

  // Walk NEXT first so unchanged/modified/added steps show up in the order
  // the approver sees them in the workflow itself.
  for (const nextStep of next) {
    const prevStep = prevById.get(nextStep.id);
    if (!prevStep) {
      steps.push(makeAddedStep(nextStep));
      continue;
    }
    const change = diffStep(prevStep, nextStep);
    if (change) steps.push(change);
  }
  // Then removed steps in their original order.
  for (const prevStep of prev) {
    if (!nextIds.has(prevStep.id)) {
      steps.push(makeRemovedStep(prevStep));
    }
  }

  return { steps, hasChanges: steps.length > 0 };
}

// ─── step diff ──────────────────────────────────────────────────────────────

function diffStep(prev: WorkflowStep, next: WorkflowStep): StepChange | null {
  const propertyChanges = diffStepProperties(prev, next);
  const sectionChanges = diffSections(prev.sections ?? [], next.sections ?? []);
  if (propertyChanges.length === 0 && sectionChanges.length === 0) return null;
  return {
    stepId: next.id,
    titleEn: next.titleEn,
    titleFr: next.titleFr,
    type: next.type,
    kind: 'modified',
    propertyChanges,
    sectionChanges,
  };
}

function makeAddedStep(step: WorkflowStep): StepChange {
  // Enumerate every section + field inside so the approver sees the full
  // "everything is new" tree. Same helper drives the v1 "first submission"
  // panel (parent passes prev=[] to diffWorkflowSteps) and the case where
  // a genuinely-new step is added between v2 and v3 — both want the same
  // depth of detail.
  return {
    stepId: step.id,
    titleEn: step.titleEn,
    titleFr: step.titleFr,
    type: step.type,
    kind: 'added',
    propertyChanges: [],
    sectionChanges: (step.sections ?? []).map(makeAddedSection),
  };
}

function makeAddedSection(section: FormSection): SectionChange {
  return {
    sectionId: section.id,
    titleEn: section.titleEn,
    titleFr: section.titleFr,
    kind: 'added',
    propertyChanges: [],
    fieldChanges: collectFields(section).map(makeAddedField),
  };
}

function makeAddedField(field: WorkflowField): FieldChange {
  return {
    fieldId: field.id,
    labelEn: field.labelEn,
    labelFr: field.labelFr,
    type: field.type,
    kind: 'added',
    propertyChanges: [],
  };
}

function makeRemovedStep(step: WorkflowStep): StepChange {
  return {
    stepId: step.id,
    titleEn: step.titleEn,
    titleFr: step.titleFr,
    type: step.type,
    kind: 'removed',
    propertyChanges: [],
    sectionChanges: (step.sections ?? []).map(makeRemovedSection),
  };
}

function makeRemovedSection(section: FormSection): SectionChange {
  return {
    sectionId: section.id,
    titleEn: section.titleEn,
    titleFr: section.titleFr,
    kind: 'removed',
    propertyChanges: [],
    fieldChanges: collectFields(section).map(makeRemovedField),
  };
}

function makeRemovedField(field: WorkflowField): FieldChange {
  return {
    fieldId: field.id,
    labelEn: field.labelEn,
    labelFr: field.labelFr,
    type: field.type,
    kind: 'removed',
    propertyChanges: [],
  };
}

function diffStepProperties(prev: WorkflowStep, next: WorkflowStep): PropertyChange[] {
  const changes: PropertyChange[] = [];
  pushIfChanged(changes, 'type', prev.type, next.type);
  pushIfChanged(changes, 'titleEn', prev.titleEn, next.titleEn);
  pushIfChanged(changes, 'titleFr', prev.titleFr, next.titleFr);
  pushIfChanged(changes, 'descriptionEn', prev.descriptionEn, next.descriptionEn);
  pushIfChanged(changes, 'descriptionFr', prev.descriptionFr, next.descriptionFr);
  // Step-type-specific configs — each is only meaningful on ONE step type,
  // but a change to any of them belongs on the diff. Serialization via
  // JSON.stringify keeps the check thorough without exploding every leaf
  // property into its own change row; approvers want the "PDF templates
  // changed" signal, they can drill into the step for detail.
  pushJsonChange(changes, 'fileEntries', prev.fileEntries ?? [], next.fileEntries ?? []);
  pushJsonChange(
    changes,
    'additionalDocumentEntries',
    prev.additionalDocumentEntries ?? [],
    next.additionalDocumentEntries ?? [],
  );
  pushIfChanged(
    changes,
    'optionalDocumentExtensions',
    (prev.optionalDocumentExtensions ?? []).slice().sort().join(', '),
    (next.optionalDocumentExtensions ?? []).slice().sort().join(', '),
  );
  pushIfChanged(
    changes,
    'optionalDocumentMaxFileSizeMb',
    prev.optionalDocumentMaxFileSizeMb ?? null,
    next.optionalDocumentMaxFileSizeMb ?? null,
  );
  pushIfChanged(
    changes,
    'showOptionalDocumentsSection',
    prev.showOptionalDocumentsSection ?? true,
    next.showOptionalDocumentsSection ?? true,
  );
  pushJsonChange(changes, 'oneSpanConfig', prev.oneSpanConfig ?? null, next.oneSpanConfig ?? null);
  pushJsonChange(changes, 'welcomeConfig', prev.welcomeConfig ?? null, next.welcomeConfig ?? null);
  pushConditionsChange(
    changes,
    'conditions',
    prev.conditions ?? [],
    next.conditions ?? [],
    prev.conditionCombinator ?? null,
    next.conditionCombinator ?? null,
  );
  return changes;
}

// ─── section diff ───────────────────────────────────────────────────────────

function diffSections(prev: readonly FormSection[], next: readonly FormSection[]): SectionChange[] {
  const prevById = new Map(prev.map((s) => [s.id, s]));
  const nextIds = new Set(next.map((s) => s.id));
  const changes: SectionChange[] = [];

  for (const nextSection of next) {
    const prevSection = prevById.get(nextSection.id);
    if (!prevSection) {
      changes.push(makeAddedSection(nextSection));
      continue;
    }
    const propertyChanges = diffSectionProperties(prevSection, nextSection);
    const fieldChanges = diffFields(collectFields(prevSection), collectFields(nextSection));
    if (propertyChanges.length === 0 && fieldChanges.length === 0) continue;
    changes.push({
      sectionId: nextSection.id,
      titleEn: nextSection.titleEn,
      titleFr: nextSection.titleFr,
      kind: 'modified',
      propertyChanges,
      fieldChanges,
    });
  }
  for (const prevSection of prev) {
    if (!nextIds.has(prevSection.id)) {
      changes.push(makeRemovedSection(prevSection));
    }
  }
  return changes;
}

function diffSectionProperties(prev: FormSection, next: FormSection): PropertyChange[] {
  const changes: PropertyChange[] = [];
  pushIfChanged(changes, 'titleEn', prev.titleEn, next.titleEn);
  pushIfChanged(changes, 'titleFr', prev.titleFr, next.titleFr);
  pushIfChanged(changes, 'repeatable', prev.repeatable ?? false, next.repeatable ?? false);
  if ((prev.repeatable ?? false) || (next.repeatable ?? false)) {
    pushIfChanged(changes, 'minInstances', prev.minInstances ?? 1, next.minInstances ?? 1);
    pushIfChanged(changes, 'maxInstances', prev.maxInstances ?? null, next.maxInstances ?? null);
    pushIfChanged(
      changes,
      'instanceLabelEn',
      prev.instanceLabelEn ?? '',
      next.instanceLabelEn ?? '',
    );
    pushIfChanged(
      changes,
      'instanceLabelFr',
      prev.instanceLabelFr ?? '',
      next.instanceLabelFr ?? '',
    );
  }
  pushConditionsChange(
    changes,
    'conditions',
    prev.conditions ?? [],
    next.conditions ?? [],
    prev.conditionCombinator ?? null,
    next.conditionCombinator ?? null,
  );
  return changes;
}

// ─── field diff ─────────────────────────────────────────────────────────────

function collectFields(section: FormSection): WorkflowField[] {
  const out: WorkflowField[] = [];
  for (const row of section.rows ?? []) {
    for (const col of row.columns ?? []) {
      if (col.field) out.push(col.field);
    }
  }
  return out;
}

function diffFields(prev: WorkflowField[], next: WorkflowField[]): FieldChange[] {
  const prevById = new Map(prev.map((f) => [f.id, f]));
  const nextIds = new Set(next.map((f) => f.id));
  const changes: FieldChange[] = [];
  for (const nextField of next) {
    const prevField = prevById.get(nextField.id);
    if (!prevField) {
      changes.push(makeAddedField(nextField));
      continue;
    }
    const propertyChanges = diffFieldProperties(prevField, nextField);
    if (propertyChanges.length === 0) continue;
    changes.push({
      fieldId: nextField.id,
      labelEn: nextField.labelEn,
      labelFr: nextField.labelFr,
      type: nextField.type,
      kind: 'modified',
      propertyChanges,
    });
  }
  for (const prevField of prev) {
    if (!nextIds.has(prevField.id)) {
      changes.push(makeRemovedField(prevField));
    }
  }
  return changes;
}

/** Stable, order-insensitive serialization of a field's PDF mappings so the
 *  diff flags an add/remove/retarget but not a mere reordering. `instanceIndex`
 *  is part of the identity — an edit that only re-routes a mapping to a
 *  different repeatable-section row must not be silently swallowed. */
function serializePdfMappings(f: WorkflowField): string {
  return (f.pdfMappings ?? [])
    .map((m) => `${m.pdfEntryId}:${m.acroFieldName}:${m.instanceIndex ?? ''}`)
    .sort()
    .join('|');
}

function diffFieldProperties(prev: WorkflowField, next: WorkflowField): PropertyChange[] {
  const changes: PropertyChange[] = [];
  // Identity / core.
  pushIfChanged(changes, 'type', prev.type, next.type);
  pushIfChanged(changes, 'labelEn', prev.labelEn, next.labelEn);
  pushIfChanged(changes, 'labelFr', prev.labelFr, next.labelFr);
  pushIfChanged(changes, 'required', prev.required, next.required);
  pushIfChanged(changes, 'placeholder', prev.placeholder ?? '', next.placeholder ?? '');
  // Length / numeric ranges.
  pushIfChanged(changes, 'minLength', prev.minLength ?? null, next.minLength ?? null);
  pushIfChanged(changes, 'maxLength', prev.maxLength ?? null, next.maxLength ?? null);
  pushIfChanged(changes, 'min', prev.min ?? null, next.min ?? null);
  pushIfChanged(changes, 'max', prev.max ?? null, next.max ?? null);
  pushIfChanged(changes, 'step', prev.step ?? null, next.step ?? null);
  pushIfChanged(changes, 'integerOnly', prev.integerOnly ?? false, next.integerOnly ?? false);
  // Validation + regex companions.
  pushIfChanged(changes, 'validate', prev.validate ?? '', next.validate ?? '');
  pushIfChanged(changes, 'regexPattern', prev.regexPattern ?? '', next.regexPattern ?? '');
  pushIfChanged(changes, 'regexMessageEn', prev.regexMessageEn ?? '', next.regexMessageEn ?? '');
  pushIfChanged(changes, 'regexMessageFr', prev.regexMessageFr ?? '', next.regexMessageFr ?? '');
  // Help + hint (bilingual).
  pushIfChanged(changes, 'hintEn', prev.hintEn ?? '', next.hintEn ?? '');
  pushIfChanged(changes, 'hintFr', prev.hintFr ?? '', next.hintFr ?? '');
  pushIfChanged(changes, 'helpTitleEn', prev.helpTitleEn ?? '', next.helpTitleEn ?? '');
  pushIfChanged(changes, 'helpTitleFr', prev.helpTitleFr ?? '', next.helpTitleFr ?? '');
  pushIfChanged(changes, 'helpBodyEn', prev.helpBodyEn ?? '', next.helpBodyEn ?? '');
  pushIfChanged(changes, 'helpBodyFr', prev.helpBodyFr ?? '', next.helpBodyFr ?? '');
  // Runtime flags.
  pushIfChanged(changes, 'readOnly', prev.readOnly ?? false, next.readOnly ?? false);
  pushIfChanged(changes, 'hideLabel', prev.hideLabel ?? false, next.hideLabel ?? false);
  pushIfChanged(
    changes,
    'verticalOptions',
    prev.verticalOptions ?? false,
    next.verticalOptions ?? false,
  );
  pushIfChanged(changes, 'maxRows', prev.maxRows ?? null, next.maxRows ?? null);
  pushIfChanged(changes, 'minAge', prev.minAge ?? null, next.minAge ?? null);
  pushIfChanged(changes, 'sourceValue', prev.sourceValue ?? '', next.sourceValue ?? '');
  // PDF mapping (multi-target, order-insensitive).
  pushIfChanged(changes, 'pdfMappings', serializePdfMappings(prev), serializePdfMappings(next));
  // Number formatting + input restrictions.
  pushIfChanged(changes, 'numberFormat', prev.numberFormat ?? '', next.numberFormat ?? '');
  pushIfChanged(
    changes,
    'inputRestriction',
    prev.inputRestriction ?? '',
    next.inputRestriction ?? '',
  );
  // Address compound.
  pushIfChanged(
    changes,
    'addressAutocomplete',
    prev.addressAutocomplete ?? '',
    next.addressAutocomplete ?? '',
  );
  pushIfChanged(changes, 'addressRole', prev.addressRole ?? '', next.addressRole ?? '');
  // Block-continue gate.
  pushIfChanged(
    changes,
    'blockingValue',
    (prev.blockingValues ?? []).slice().sort().join(', '),
    (next.blockingValues ?? []).slice().sort().join(', '),
  );
  pushIfChanged(
    changes,
    'blockingValueOperator',
    prev.blockingValueOperator ?? '',
    next.blockingValueOperator ?? '',
  );
  pushIfChanged(
    changes,
    'blockContinueAlert',
    prev.blockContinueAlert ?? false,
    next.blockContinueAlert ?? false,
  );
  pushIfChanged(
    changes,
    'blockContinueMessageEn',
    prev.blockContinueMessageEn ?? '',
    next.blockContinueMessageEn ?? '',
  );
  pushIfChanged(
    changes,
    'blockContinueMessageFr',
    prev.blockContinueMessageFr ?? '',
    next.blockContinueMessageFr ?? '',
  );
  // Cross-field wiring.
  pushIfChanged(changes, 'uniquenessGroup', prev.uniquenessGroup ?? '', next.uniquenessGroup ?? '');
  pushIfChanged(
    changes,
    'minSelections',
    prev.minSelections?.toString() ?? '',
    next.minSelections?.toString() ?? '',
  );
  pushIfChanged(
    changes,
    'sourceField',
    formatSourceField(prev.sourceField),
    formatSourceField(next.sourceField),
  );
  // Required-empty override copy.
  pushIfChanged(
    changes,
    'requiredMessageEn',
    prev.requiredMessageEn ?? '',
    next.requiredMessageEn ?? '',
  );
  pushIfChanged(
    changes,
    'requiredMessageFr',
    prev.requiredMessageFr ?? '',
    next.requiredMessageFr ?? '',
  );
  // Layout-block styling (StaticText / Paragraph / BulletedList).
  pushIfChanged(changes, 'fontSize', prev.fontSize ?? null, next.fontSize ?? null);
  pushIfChanged(changes, 'color', prev.color ?? '', next.color ?? '');
  pushIfChanged(changes, 'marginTop', prev.marginTop ?? null, next.marginTop ?? null);
  pushIfChanged(changes, 'marginBottom', prev.marginBottom ?? null, next.marginBottom ?? null);
  pushIfChanged(changes, 'fontWeight', prev.fontWeight ?? '', next.fontWeight ?? '');
  pushIfChanged(changes, 'listStyle', prev.listStyle ?? '', next.listStyle ?? '');
  // Notice-specific chrome.
  pushIfChanged(changes, 'backgroundColor', prev.backgroundColor ?? '', next.backgroundColor ?? '');
  pushIfChanged(changes, 'icon', prev.icon ?? '', next.icon ?? '');
  pushIfChanged(changes, 'displayAs', prev.displayAs ?? '', next.displayAs ?? '');
  // Image-specific.
  pushIfChanged(changes, 'imageUrl', prev.imageUrl ?? '', next.imageUrl ?? '');
  pushIfChanged(changes, 'imageWidthSize', prev.imageWidthSize ?? '', next.imageWidthSize ?? '');
  pushIfChanged(changes, 'imageAlignment', prev.imageAlignment ?? '', next.imageAlignment ?? '');
  pushIfChanged(changes, 'captionEn', prev.captionEn ?? '', next.captionEn ?? '');
  pushIfChanged(changes, 'captionFr', prev.captionFr ?? '', next.captionFr ?? '');
  pushIfChanged(changes, 'altEn', prev.altEn ?? '', next.altEn ?? '');
  pushIfChanged(changes, 'altFr', prev.altFr ?? '', next.altFr ?? '');
  // RepeatableRow structure + cross-row sum rule.
  pushJsonChange(
    changes,
    'repeatableTemplate',
    prev.repeatableTemplate ?? null,
    next.repeatableTemplate ?? null,
  );
  pushJsonChange(changes, 'sumValidation', prev.sumValidation ?? null, next.sumValidation ?? null);
  pushOptionsChange(changes, prev.options ?? [], next.options ?? []);
  pushConditionsChange(
    changes,
    'conditions',
    prev.conditions ?? [],
    next.conditions ?? [],
    prev.conditionCombinator ?? null,
    next.conditionCombinator ?? null,
  );
  return changes;
}

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Push a property change onto the list when the two sides differ. Booleans /
 * numbers / null get stringified through {@link formatScalar} so the panel
 * can render "true → false" / "empty → v1" verbatim.
 */
function pushIfChanged<T>(changes: PropertyChange[], key: string, prev: T, next: T): void {
  if (deepEqual(prev, next)) return;
  changes.push({ key, before: formatScalar(prev), after: formatScalar(next) });
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  return String(v);
}

function formatSourceField(ref: WorkflowField['sourceField']): string {
  if (!ref || !ref.fieldId) return '';
  return `${ref.stepId}:${ref.fieldId}`;
}

/**
 * Options list diff — represented as a single "options" property change
 * carrying compact before/after summaries (e.g. "A, B, C" vs "A, B, C, D").
 * The panel formats this as a one-line change rather than a nested list,
 * because 90% of option edits are add-one / rename-one / reword-label.
 *
 * Change DETECTION runs against a full serialization so non-label edits
 * (per-item help title/body, revealFieldId, clearsOnSelect, per-option
 * internalName for PDF mapping, French labels) still register — even
 * though the SUMMARY shown to the approver is label-only. That keeps the
 * "1 line per property" UX while making sure a "reveal added to option 3"
 * change doesn't slip past.
 */
function pushOptionsChange(
  changes: PropertyChange[],
  prev: readonly SelectOption[],
  next: readonly SelectOption[],
): void {
  if (serializeOptions(prev) === serializeOptions(next)) return;
  changes.push({
    key: 'options',
    before: summariseOptions(prev),
    after: summariseOptions(next),
  });
}

function serializeOptions(options: readonly SelectOption[]): string {
  // Order-preserving so a reorder counts as a change (the runtime renders
  // options in this order). Every leaf property is included so a per-item
  // reveal / help / PDF-mapping edit doesn't get lost under a shared
  // label. Missing fields ride as empty strings so undefined vs "" doesn't
  // flip the diff on unchanged options. pdfMappings sorted so a reorder of
  // per-option checkbox targets doesn't register as a false positive.
  return JSON.stringify(
    options.map((o) => ({
      value: o.value ?? '',
      valueFr: o.valueFr ?? '',
      labelEn: o.labelEn ?? '',
      labelFr: o.labelFr ?? '',
      helpTitleEn: o.helpTitleEn ?? '',
      helpTitleFr: o.helpTitleFr ?? '',
      helpBodyEn: o.helpBodyEn ?? '',
      helpBodyFr: o.helpBodyFr ?? '',
      revealFieldId: o.revealFieldId ?? '',
      // Card-layout icon key. Included so swapping an option's icon shows up
      // in the approver's version diff — an icon change is a visible change
      // to what the advisor sees, and omitting it here would make the edit
      // silently invisible at review time (same class of miss as the
      // pdfMappings instanceIndex gap fixed on 2026-07-21).
      icon: o.icon ?? '',
      clearsOnSelect: (o.clearsOnSelect ?? []).slice().sort().join(','),
      pdfMappings: (o.pdfMappings ?? [])
        .map((m) => `${m.pdfEntryId}:${m.acroFieldName}:${m.instanceIndex ?? ''}`)
        .sort()
        .join('|'),
    })),
  );
}

function summariseOptions(options: readonly SelectOption[]): string {
  if (options.length === 0) return '';
  return options.map((o) => o.labelEn || o.value || '—').join(', ');
}

/**
 * Sum-total change for a nested object we don't want to expand per-leaf
 * (step-type-specific configs, repeatable templates, sum-validation rules).
 * Stringifies both sides through JSON so structural equality is stable,
 * then shows a short "changed" placeholder to the approver — they can drill
 * into the step / field for detail. Objects with the same content but
 * different key order will register as different; we accept that noise
 * because our authoring code doesn't reorder keys.
 */
function pushJsonChange(
  changes: PropertyChange[],
  key: string,
  prev: unknown,
  next: unknown,
): void {
  if (deepEqual(prev ?? null, next ?? null)) return;
  changes.push({
    key,
    before: prev == null ? 'none' : 'changed',
    after: next == null ? 'none' : 'changed',
  });
}

/**
 * Conditions list diff — represents visibility gating changes as
 * "conditions changed" with the human-readable count on each side. The
 * panel doesn't drill into which specific rule changed; the approver can
 * hit the peek popover on the step / field itself to see the current
 * rules (that view already exists — `collectStepConditions`). Point here
 * is just to draw attention.
 */
function pushConditionsChange(
  changes: PropertyChange[],
  key: string,
  prev: readonly FileEntryCondition[],
  next: readonly FileEntryCondition[],
  prevCombinator: 'all' | 'any' | null,
  nextCombinator: 'all' | 'any' | null,
): void {
  if (deepEqual(prev, next) && prevCombinator === nextCombinator) return;
  changes.push({
    key,
    before: summariseConditions(prev, prevCombinator),
    after: summariseConditions(next, nextCombinator),
  });
}

function summariseConditions(
  conditions: readonly FileEntryCondition[],
  combinator: 'all' | 'any' | null,
): string {
  if (conditions.length === 0) return 'none';
  const suffix = combinator === 'any' ? ' (any)' : combinator === 'all' ? ' (all)' : '';
  return `${conditions.length} rule${conditions.length === 1 ? '' : 's'}${suffix}`;
}

/** Structural equality via canonical JSON.stringify. Used for shallow
 *  scalars + small arrays / objects — never a bottleneck at this scale. */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}
